// ======================================================================
// 📁 ProductionPlanningDrawer.js
// ERP-grade Production Planning Drawer (REFRACTORED + PATCHED - FINAL)
// ======================================================================

import React, { useEffect, useState, useRef, useCallback } from "react";

import MaterialsCard from "./components-drawer/MaterialsCard";
import ToolsAssignmentCard from "./components-drawer/ToolsAssignmentCard";
import WorkerAssignmentCard from "./components-drawer/WorkerAssignmentCard";
import WorkingAreaAssignmentCard from "./components-drawer/WorkingAreaAssignmentCard";
import MachineAssignmentCard from "./components-drawer/MachineAssignmentCard";
import ValidatePlanDrawer from "./validation/ValidatePlanDrawer";

import "./production-planning.css";

import { hasAcceptedSchedule } from "./drawer-planning.timeline";


import { mapBackendOperations } from "./drawer-planning.mapper";

import ProductionPlanningGantt from "./gantt/ProductionPlanningGantt";

import {
  getPlanningContext,
  savePlanningDraft,
  validatePlanningDraft,
  getWorkingAreas,
   applyValidatedScheduleToDraft
} from "./drawer-planning.api";

import {
  applyValidatedSchedule,
} from "./drawer-planning.validation";

import {
  submitPlanForApproval
} from "./drawer-planning.api";


const EMPTY_OPERATION = {
  seq: 0,
  name: "",
  plannedMinutes: 0,
  from: "",
  to: "",
  machines: [],
  workers: [],
  tools: [],
  materials: [],
  workingAreas: [],
};


export default function ProductionPlanningDrawer({
  open,
  orderItem,
  onClose,
}) {
  const [operations, setOperations] = useState([]);
  const [selectedSeq, setSelectedSeq] = useState(null);

  const [draftId, setDraftId] = useState(null);
  const [scheduleLocked, setScheduleLocked] = useState(false);
  /* ============================================================
    APPROVAL GOVERNANCE PATCH
    Timestamp: 2026-01-24 23:05 IST
    ============================================================ */
  const [approval, setApproval] = useState(null);
  /* ============================================================ */

  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const [availableTools, setAvailableTools] = useState([]);
  const [availableWorkingAreas, setAvailableWorkingAreas] = useState([]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGantt, setShowGantt] = useState(false);

  const tableRef = useRef(null);

  /* ============================================================
    DERIVED EDIT LOCK
    Timestamp: 2026-01-24 23:05 IST
    ============================================================ */
    const editingLocked =
    approval?.derived_state === "PENDING" ||
    approval?.status_code === "APPROVED";
  /* ============================================================ */

  // ------------------------------------------------------------
  // LOAD CONTEXT
  // ------------------------------------------------------------
  useEffect(() => {
  if (!open || !orderItem?.order_item_id) return;

  // ============================================================
  // 1️⃣ LOAD PLANNING CONTEXT (routing + draft + materials)
  // ============================================================
  getPlanningContext(orderItem.order_item_id)
    .then((res) => {
      const data = res.data?.data || {};
      const snapshot = data?.planning_snapshot || {};
      /* ============================================================
        APPROVAL LOAD
        Timestamp: 2026-01-24 23:05 IST
        ============================================================ */
      setApproval(data?.approval || null);
      /* ============================================================ */

      setDraftId(snapshot?.draft_id || null);

      // tools come from prepare
          setAvailableTools(
      (data?.master_tools || []).map((t) => ({
        tool_id: t.tool_id ?? t.id,
        tool_name: t.tool_name,
      }))
    );

      const backendOps =
        snapshot?.operations?.length
          ? snapshot.operations
          : data?.routing?.operations || [];

      const mapped = mapBackendOperations(
        backendOps,
        data?.materials || [],
        data?.master_tools || []
      );

      const accepted =
        snapshot?.schedule_accepted === true &&
            Array.isArray(snapshot?.operations) &&
            snapshot.operations.length > 0;

      setScheduleLocked(accepted);

      if (accepted || snapshot?.draft_loaded) {
        setOperations(mapped);
        setSelectedSeq(mapped[0]?.seq ?? null);
      } else {
        
        setOperations(mapped);
        setSelectedSeq(mapped[0]?.seq ?? null);

      }
    })
    .catch(() => {
      setOperations([]);
      setSelectedSeq(null);
    });

  
    
  // ============================================================
  // 2️⃣ LOAD WORKING AREA MASTER (SEPARATE API)
  // ============================================================
  getWorkingAreas()
    .then((res) => {
      setAvailableWorkingAreas(res.data?.data || []);
    })
    .catch(() => {
      setAvailableWorkingAreas([]);
    });

}, [open, orderItem]);

// ============================================================
// REFRESH APPROVAL STATUS (EMAIL POLLING)
// ============================================================
const refreshApprovalStatus = useCallback(() => {
  if (!orderItem?.order_item_id) return;

  getPlanningContext(orderItem.order_item_id)
    .then((res) => {
      const data = res.data?.data || {};
      setApproval(data?.approval || null);
    })
    .catch(() => {});
}, [orderItem?.order_item_id]);


// ============================================================
// AUTO REFRESH WHILE STATUS = SUBMITTED
// ============================================================
useEffect(() => {
  if (!approval) return;

  // ✅ only while waiting for approval
  if (approval.status_code !== "SUBMITTED") return;

  const interval = setInterval(() => {
    refreshApprovalStatus();
  }, 4000);

  return () => clearInterval(interval);

}, [approval?.status_code, refreshApprovalStatus]);


  // ------------------------------------------------------------
// SAVE DRAFT
// ------------------------------------------------------------
  const onSaveDraft = async () => {
  try {
    const payload = {
      order_item_id: orderItem.order_item_id,
      routing_assignment_id: orderItem.routing_id,

      operations: operations.map((op) => ({
        operation_seq_no: op.seq,
        routing_assignment_operation_id:
          op.routingAssignmentOperationId,

        planned_duration_minutes: op.plannedMinutes,
        planned_start_datetime: op.from,
        planned_end_datetime: op.to,

        machines: op.machines || [],
        workers: op.workers || [],
        tools: (op.tools || []).map((t) => t.tool_id),
        working_areas: op.workingAreas || [],
      })),
    };

    const res = await savePlanningDraft(payload);

    const id =
      res.data?.data?.draft_id ||
      res.data?.draft_id;

    if (id) {
      setDraftId(id);
    }

    // ✅ SUCCESS FEEDBACK
    const message =
      res.data?.message || "Draft saved successfully";

    alert(message);

    return id;
  } catch (err) {
    console.error("Save draft failed", err);
    alert("Failed to save draft");
    throw err;
  }
};

  // ------------------------------------------------------------
  // VALIDATE PLAN (auto-save restored)
  // ------------------------------------------------------------
  const onValidatePlan = async () => {
    try {
      setValidating(true);

      const id = draftId || (await onSaveDraft());

      const res = await validatePlanningDraft(id);

      setValidationResult(res.data?.data);
      setValidationOpen(true);
    } finally {
      setValidating(false);
    }
  };

  // ------------------------------------------------------------
// SEND FOR APPROVAL
// ------------------------------------------------------------
  const onSendForApproval = async () => {
    try {
      await submitPlanForApproval(draftId);

      // reload planning context
      const ctx = await getPlanningContext(orderItem.order_item_id);
      const data = ctx.data?.data || {};

      setApproval(data?.approval || null);

      alert("Plan submitted for approval");

    } catch (err) {
      console.error(err);
      alert("Failed to submit for approval");
    }
  };


  const selectedOp =
    operations.find((o) => o.seq === selectedSeq) ||
    operations[0] ||
    EMPTY_OPERATION;



  if (!open || !orderItem?.order_item_id) return null;

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <>
      {!isFullscreen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 1040,
          }}
        />
      )}

      {/* ================= DRAWER ================= */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100%",
          width: isFullscreen ? "100%" : "75%",
          maxWidth: 1400,
          background: "#f8fafc",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <div className="p-3 border-bottom bg-dark text-white d-flex justify-content-between">
          <strong>
            Planning – {orderItem.orderNo} / {orderItem.itemCode}
          </strong>
          <div>
            <button
              className="btn btn-sm btn-light me-2"
              onClick={() => setIsFullscreen((p) => !p)}
            >
              ⤢
            </button>
            <button
              className="btn btn-sm btn-light"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>
        {/* ============================================================
        APPROVAL STATUS BANNER
        Timestamp: 2026-01-24 23:05 IST
      ============================================================ */}
      {/* ============================================================
          APPROVAL STATUS BANNER
      ============================================================ */}
      {approval && (
        <div
          className={`alert m-2 ${
            approval.status_code === "APPROVED"
              ? "alert-success"
              : approval.status_code === "REJECTED"
              ? "alert-danger"
              : approval.status_code === "SUBMITTED"
              ? "alert-warning"
              : "alert-secondary"
          }`}
        >
          <strong>Approval Status:</strong>{" "}
          {approval.status_label || approval.status_code}
        </div>
      )}


        {/* BODY */}
        <div className="d-flex flex-grow-1 overflow-hidden">
          {/* LEFT */}
          <div
            className="border-end bg-white"
            style={{ width: "38%" }}
            ref={tableRef}
            tabIndex={0}
          >
            <table className="table table-sm mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Seq</th>
                  <th>Operation</th>
                  <th>Predecessor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {operations.map((op) => (
                  <tr
                    key={op.seq}
                    className={
                      selectedSeq === op.seq
                        ? "table-active"
                        : ""
                    }
                    onClick={() => setSelectedSeq(op.seq)}
                  >
                    <td>{op.seq}</td>
                    <td>{op.name}</td>
                    <td className="text-muted small">
                      {op.predecessor
                        ? `${op.predecessor.operation_seq_no} – ${op.predecessor.operation_name}`
                        : "—"}
                    </td>
                    <td>
                      <span
                        className={`badge bg-${
                          op.readiness === "BLOCKED"
                            ? "danger"
                            : "success"
                        }`}
                      >
                        {op.readiness || "READY"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* RIGHT */}
          <div className="flex-grow-1 p-3 overflow-auto">
            <div className="card mb-3">
              <div className="card-body row g-3">
                <div className="col-md-4">
                  <label>Planned Minutes</label>

                    <input
                      type="number"
                      min={1}
                      className="form-control"
                      value={selectedOp.plannedMinutes}
                      //disabled={scheduleLocked}
                      disabled={editingLocked}
                      onChange={(e) => {
                        const minutes = Number(e.target.value || 0);

                        setOperations((prev) =>
                          prev.map((op) =>
                            op.seq === selectedOp.seq
                              ? {
                                  ...op,
                                  plannedMinutes: minutes,
                                }
                              : op
                          )
                        );
                      }}
                    />


                </div>


                <div className="col-md-4">
                  <label>From</label>
                  <input
                    className="form-control"
                    readOnly
                    value={
                      selectedOp.from
                        ? new Date(
                            selectedOp.from
                          ).toLocaleString()
                        : ""
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label>To</label>
                  <input
                    className="form-control"
                    readOnly
                    value={
                      selectedOp.to
                        ? new Date(
                            selectedOp.to
                          ).toLocaleString()
                        : ""
                    }
                  />
                </div>
              </div>
            </div>

            <MachineAssignmentCard
  {...selectedOp}
  onChange={(payload) => {
    setOperations((prev) =>
      prev.map((op) =>
        op.seq === selectedOp.seq
          ? {
    ...op,   // 🔥 critical — keeps routing data

    machines:
      payload.machines !== undefined
        ? payload.machines
        : op.machines,

    selectedMachineId:
      payload.selectedMachineId !== undefined
        ? payload.selectedMachineId
        : op.selectedMachineId,
  }

          : op
      )
    );
  }}
/>

            <WorkingAreaAssignmentCard
              availableAreas={availableWorkingAreas}
              selectedAreas={selectedOp.workingAreas || []}

              onAddArea={(area) => {
                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          workingAreas: [
                            ...(op.workingAreas || []),
                            area,
                          ],
                        }
                      : op
                  )
                );
              }}

              onRemoveArea={(areaId) => {
                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          workingAreas: (op.workingAreas || []).filter(
                            (a) => a.working_area_id !== areaId
                          ),
                        }
                      : op
                  )
                );
              }}

              onUpdateArea={(areaId, updatedArea) => {
                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          workingAreas: (op.workingAreas || []).map(
                            (a) =>
                              a.working_area_id === areaId
                                ? updatedArea
                                : a
                          ),
                        }
                      : op
                  )
                );
              }}
            />

            <WorkerAssignmentCard
              skill={selectedOp.skill}
              workersBySkill={selectedOp.workersBySkill}

              selectedWorkerId={selectedOp.selectedWorkerId}
              selectedWorkers={selectedOp.workers || []}

              onPrimaryChange={(workerId) => {
                const id = Number(workerId);

                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          selectedWorkerId: id,
                          workers: id
                            ? [
                                id,
                                ...(op.workers || []).filter(
                                  (w) => w !== id
                                ),
                              ]
                            : [],
                        }
                      : op
                  )
                );
              }}

              onAddWorker={(workerId) => {
                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          workers: [
                            ...(op.workers || []),
                            workerId,
                          ],
                        }
                      : op
                  )
                );
              }}

              onRemoveWorker={(workerId) => {
                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          workers: (op.workers || []).filter(
                            (w) => w !== workerId
                          ),
                        }
                      : op
                  )
                );
              }}
            />

            <MaterialsCard materials={selectedOp.materials} />
            <ToolsAssignmentCard
                availableTools={availableTools}
                selectedTools={selectedOp.tools || []}

              onAddTool={(toolId) => {
                const toolMeta = availableTools.find(
                  (t) => t.tool_id === toolId
                );

                if (!toolMeta) return;

                setOperations((prev) =>
                  prev.map((op) =>
                    op.seq === selectedOp.seq
                      ? {
                          ...op,
                          tools: [
                            ...(op.tools || []),
                            toolMeta,   // ✅ object
                          ],
                        }
                      : op
                  )
                );
              }}


                onRemoveTool={(toolId) => {
                  setOperations((prev) =>
                    prev.map((op) =>
                      op.seq === selectedOp.seq
                        ? {
                            ...op,
                            tools: (op.tools || []).filter(
                              (t) =>
                                (typeof t === "object"
                                  ? t.tool_id
                                  : t) !== toolId
                            ),
                          }
                        : op
                    )
                  );
                }}

              />

          </div>
        </div>
        {/* ================= GANTT ================= */}
        <div className="bg-white border-top">

          <div
            className="p-2 bg-light fw-semibold"
            style={{ cursor: "pointer" }}
            onClick={() => setShowGantt((p) => !p)}
          >
            {showGantt ? "▾ Hide" : "▸ Show"} Gantt Timeline
          </div>

          {showGantt && (
            <div className="p-3">
              <ProductionPlanningGantt
                operations={operations}
                selectedSeq={selectedSeq}
                onSelect={setSelectedSeq}
              />
            </div>
          )}

        </div>
        
       {/* ================= FOOTER ================= */}
        <div className="p-2 bg-white border-top text-end">

          {/* ============================================================
              SEND FOR APPROVAL
              Timestamp: 2026-01-24 23:05 IST
            ============================================================ */}
          {/* ============================================================
              SEND FOR APPROVAL
              ============================================================ */}
          {/* ============================================================
              SEND FOR APPROVAL
              Timestamp: 2026-01-25 02:10 IST
            ============================================================ */}
              {scheduleLocked && (!approval || approval.status_code === "REJECTED")  && (
                <button
                  className="btn btn-warning me-2"
                  onClick={async () => {
                    try {
                      await submitPlanForApproval(draftId);

                      alert("Production plan submitted for approval");

                      // 🔁 reload context
                      const ctx = await getPlanningContext(orderItem.order_item_id);
                      const data = ctx.data?.data || {};

                      setApproval(data?.approval || null);

                    } catch (err) {
                      console.error(err);
                      alert("Failed to submit plan for approval");
                    }
                  }}
                >
                  Send for Approval
                </button>
              )}
  


          {/* SAVE DRAFT */}
          <button
            className="btn btn-outline-secondary me-2"
            disabled={editingLocked  || validating}
            onClick={onSaveDraft}
          >
            Save Draft
          </button>

          {/* VALIDATE PLAN */}
          <button
            className="btn btn-outline-primary me-2"
            disabled={editingLocked || validating}
            onClick={onValidatePlan}
          >
            Validate Plan
          </button>

          {/* RELEASE — ONLY AFTER APPROVAL */}
          {approval?.status_code === "APPROVED" && (
            <button className="btn btn-success">
              Release Operations
            </button>
          )}

        </div>
   

        
                

      </div>

      <ValidatePlanDrawer
        open={validationOpen}
        validationResult={validationResult}
        onClose={() => setValidationOpen(false)}
        onAccept={async (res) => {
          if (!draftId) return;

          // ✅ transform validation response → backend format
          const acceptedOperations = (res?.operations || []).map(
            (op) => ({
              operation_seq_no: op.operation_seq_no,
              draft_operation_id: op.draft_operation_id,

              suggested_start_datetime: op.suggested_start_datetime,
              suggested_end_datetime: op.suggested_end_datetime,
              planned_minutes: op.planned_minutes,

              
            })
          );

          try {
            // 1️⃣ save to DB
            await applyValidatedScheduleToDraft(
              draftId,
              acceptedOperations
            );

            // 🔁 reload planning context
            const ctx = await getPlanningContext(orderItem.order_item_id);
            const data = ctx.data?.data || {};
            const snapshot = data?.planning_snapshot || {};

            setScheduleLocked(snapshot?.schedule_accepted === true);
            setApproval(data?.approval || null);


            // 2️⃣ update UI
            setOperations((prev) =>
              applyValidatedSchedule(prev, acceptedOperations)
            );

            alert("Validated schedule saved successfully");

          } catch (err) {
            console.error(err);
            alert("Failed to save validated schedule");
          }
        }}
      />

    </>
  );
}
